import React, { useState, useEffect } from "react";
import { auth } from "../config/config";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Button, Tabs, Tab, Box, Typography, Link, CircularProgress
} from "@mui/material";

function FirebaseUiEmailButton({ onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="firebaseui-idp-button mdl-button mdl-js-button firebaseui-idp-password"
      style={{
        width: "81%",
        backgroundColor: "#DB4437",
        color: "white",
        padding: "15px",
        height: "40px",
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-start",
        margin: "8px auto 0",
        boxShadow: "1px 1px 2px rgba(0, 0, 0, 0.3)",
      }}
    >
      <span className="firebaseui-idp-icon-wrapper" aria-hidden>
        <svg className="firebaseui-idp-icon" viewBox="0 0 24 24" width="18" height="18">
          <path fill="white" d="M20 4H4c-1.1 0-2 .9-2 2v12a2 2 0 0 0 2 2h16c1.11 0 2-.89 2-2V6a2 2 0 0 0-2-2zm0 4-8 5-8-5V6l8 5 8-5v2z" />
        </svg>
      </span>
      <span className="firebaseui-idp-text firebaseui-idp-text-long">Sign in with email</span>
      <span className="firebaseui-idp-text firebaseui-idp-text-short">Email</span>
    </button>
  );
}

export default function EmailPasswordAuth() {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState(0);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [pwd, setPwd] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  const [providersVisible, setProvidersVisible] = useState(false);

  useEffect(() => {
    const root =
      document.querySelector("#firebaseui_container") ||
      document.querySelector(".firebaseui-container") ||
      document;

    const compute = () => {
      const list = root.querySelector?.(".firebaseui-idp-list");
      const visible = !!(list && list.offsetParent !== null);
      setProvidersVisible(visible);
    };

    compute();
    const mo = new MutationObserver(compute);
    mo.observe(root, { childList: true, subtree: true, attributes: true, attributeFilter: ["class", "style"] });
    window.addEventListener("resize", compute);

    return () => {
      mo.disconnect();
      window.removeEventListener("resize", compute);
    };
  }, []);

  const resetState = () => {
    setTab(0); setName(""); setEmail(""); setPwd(""); setMsg(""); setBusy(false);
  };

  const genericSigninError = "Incorrect email or password.";
  const genericSignupError = "We couldn’t create that account. If you already have one, sign in or reset your password.";

  const handleSignin = async () => {
    setBusy(true); setMsg("");
    try {
      await auth.signInWithEmailAndPassword(email.trim(), pwd);
      setOpen(false); resetState();
    } catch (e) {
      const code = e?.code || "";
      if (code === "auth/invalid-email") setMsg("Please enter a valid email address.");
      else if (code === "auth/too-many-requests") setMsg("Too many attempts. Please try again later.");
      else setMsg(genericSigninError);
    } finally {
      setBusy(false);
    }
  };

  const handleSignup = async () => {
    setBusy(true); setMsg("");
    try {
      const cred = await auth.createUserWithEmailAndPassword(email.trim(), pwd);
      if (name.trim()) await cred.user.updateProfile({ displayName: name.trim() });
      setOpen(false); resetState();
    } catch (e) {
      const code = e?.code || "";
      if (code === "auth/invalid-email") setMsg("Please enter a valid email address.");
      else if (code === "auth/weak-password") setMsg("Use a stronger password.");
      else setMsg(genericSignupError);
    } finally {
      setBusy(false);
    }
  };

  const handleReset = async () => {
    setBusy(true); setMsg("");
    try {
      await auth.sendPasswordResetEmail(email.trim());
      setMsg("If an account exists for this email, a reset link has been sent.");
    } catch {
      setMsg("If an account exists for this email, a reset link has been sent.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {providersVisible && <FirebaseUiEmailButton onClick={() => setOpen(true)} />}

      <Dialog open={open} onClose={() => { setOpen(false); resetState(); }} fullWidth maxWidth="xs">
        <DialogTitle>{tab === 0 ? "Sign in" : "Create account"}</DialogTitle>
        <DialogContent>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (tab === 0) handleSignin();
              else handleSignup();
            }}
          >
            <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
              <Tab label="Sign in" />
              <Tab label="Create account" />
            </Tabs>

            {tab === 1 && (
              <TextField
                label="Full name (optional)"
                value={name}
                onChange={e => setName(e.target.value)}
                margin="dense"
                fullWidth
              />
            )}
            <TextField
              label="Email"
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              autoComplete="username"
              margin="dense"
              fullWidth
            />
            <TextField
              label="Password"
              type="password"
              value={pwd}
              onChange={e => setPwd(e.target.value)}
              autoComplete={tab === 0 ? "current-password" : "new-password"}
              margin="dense"
              fullWidth
            />

            {msg && (
              <Box mt={1}><Typography color="error" variant="body2">{msg}</Typography></Box>
            )}

            {tab === 0 && (
              <Box mt={1}>
                <Link component="button" type="button" onClick={handleReset}>
                  Forgot password?
                </Link>
              </Box>
            )}

            <button type="submit" style={{ display: "none" }} />
          </form>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setOpen(false); resetState(); }} disabled={busy}>Cancel</Button>
          <Button
            variant="contained"
            onClick={tab === 0 ? handleSignin : handleSignup}
            disabled={busy || !email || !pwd}
          >
            {busy ? <CircularProgress size={18} /> : (tab === 0 ? "Continue" : "Create account")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
